import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Logo } from './Logo';

const linkClass =
  'text-[15px] font-medium text-[#1C1E21] hover:text-[#0064E0] transition-colors';

const colHeaderClass =
  'mb-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#65676B]';

const slashClass = 'font-bold text-[#0064E0]';

const liveBadgeClass =
  'ml-1.5 inline-block rounded bg-[#E6F9F2] px-1.5 py-0.5 text-[11px] font-semibold text-[#00a86b]';

const newBadgeClass =
  'ml-1.5 inline-block rounded bg-[#E7F0FF] px-1.5 py-0.5 text-[11px] font-semibold text-[#0064E0]';

const platformRowClass =
  'block w-full cursor-default rounded-lg border-0 bg-transparent px-3 py-2 text-left transition-colors hover:bg-[#F0F2F5]';

const solutionsRowClass =
  'block w-full cursor-default rounded-lg border-0 bg-transparent px-3 py-2 text-left text-[15px] font-medium text-[#1C1E21] transition-colors hover:bg-[#F0F2F5]';

type OpenMenu = null | 'platform' | 'solutions';

const mobileProtectStackItems: [string, string, boolean][] = [
  ['NetGuard', 'Stop threats before they enter', false],
  ['MailShield', 'AI-powered email defence', false],
  ['CloudWatch', 'Secure every cloud workload', false],
  ['AgentArmor', 'Safe AI, safe business', true],
  ['GridSentry', 'Protection for critical infrastructure', false],
  ['TrustID', "Know who's inside your network", false],
  ['EdgeDefend', 'Security on every device', false],
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [mobilePlatformOpen, setMobilePlatformOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);

  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const scheduleCloseMenu = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setOpenMenu(null);
      hideTimerRef.current = null;
    }, 150);
  }, [clearHideTimer]);

  const openDropdown = useCallback(
    (menu: 'platform' | 'solutions') => {
      clearHideTimer();
      setOpenMenu(menu);
    },
    [clearHideTimer]
  );

  useEffect(() => {
    return () => clearHideTimer();
  }, [clearHideTimer]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const headerBarClass = scrolled
    ? 'border-b border-[#E4E6EB] bg-white shadow-[var(--shadow-md)]'
    : 'border-b border-transparent bg-transparent';

  const triggerClass = (active: boolean) =>
    `flex items-center gap-1 border-0 bg-transparent text-[15px] font-medium transition-colors ${
      active ? 'text-[#0064E0]' : 'text-[#1C1E21] hover:text-[#0064E0]'
    }`;

  const panelClass =
    'rounded-[12px] border border-[#E4E6EB] bg-white p-8 shadow-[0_8px_24px_rgba(0,0,0,0.12)]';

  return (
    <header className={`sticky top-0 z-50 transition-all duration-200 ${headerBarClass}`}>
      <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3.5 md:px-8">
        <Logo size="md" />

        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 md:flex">
          {/* Platform mega-menu */}
          <div
            className="relative"
            onMouseEnter={() => openDropdown('platform')}
            onMouseLeave={scheduleCloseMenu}
          >
            <button type="button" className={triggerClass(openMenu === 'platform')} aria-expanded={openMenu === 'platform'}>
              Platform
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden className="opacity-70">
                <path d="M6 8L2 4h8L6 8z" />
              </svg>
            </button>
            {openMenu === 'platform' && (
              <div className="absolute left-1/2 top-full z-[100] -translate-x-1/2 pt-2">
                <div className={`${panelClass} w-[min(100vw-2rem,56rem)]`}>
                  <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
                    <div>
                      <p className={colHeaderClass}>Protect Your Stack</p>
                      <ul className="space-y-0.5">
                        <li>
                          <a
                            href="https://shieldsme.lognsec.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={platformRowClass}
                          >
                            <span className="text-[15px] font-semibold text-[#1C1E21]">
                              <span className={slashClass}>/</span> ShieldSME
                              <span className={liveBadgeClass}>LIVE</span>
                            </span>
                            <span className="mt-0.5 block text-[13px] text-[#65676B]">
                              Security health for your business
                            </span>
                          </a>
                        </li>
                        {[
                          ['NetGuard', 'Stop threats before they enter'],
                          ['MailShield', 'AI-powered email defence'],
                          ['CloudWatch', 'Secure every cloud workload'],
                          ['AgentArmor', 'Safe AI, safe business', 'new' as const],
                          ['GridSentry', 'Protection for critical infrastructure'],
                          ['TrustID', "Know who's inside your network"],
                          ['EdgeDefend', 'Security on every device'],
                        ].map(([name, sub, badge]) => (
                          <li key={name}>
                            <button type="button" className={platformRowClass}>
                              <span className="text-[15px] font-semibold text-[#1C1E21]">
                                <span className={slashClass}>/</span> {name}
                                {badge === 'new' ? <span className={newBadgeClass}>NEW</span> : null}
                              </span>
                              <span className="mt-0.5 block text-[13px] text-[#65676B]">{sub}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className={colHeaderClass}>Strengthen Your Posture</p>
                      <ul className="space-y-0.5">
                        {[
                          ['ExposureIQ', 'Find and fix before attackers do'],
                          ['HumanLayer', 'Your people, defended'],
                          ['SurfaceMap', 'Know your full attack surface'],
                          ['ForensIQ', 'Investigate with precision'],
                          ['ResilienceOS', 'Prepare, respond, recover'],
                        ].map(([name, sub]) => (
                          <li key={name}>
                            <button type="button" className={platformRowClass}>
                              <span className="text-[15px] font-semibold text-[#1C1E21]">
                                <span className={slashClass}>/</span> {name}
                              </span>
                              <span className="mt-0.5 block text-[13px] text-[#65676B]">{sub}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className={colHeaderClass}>AI-Powered Intelligence</p>
                      <ul className="space-y-0.5">
                        <li>
                          <button type="button" className={platformRowClass}>
                            <span className="text-[15px] font-semibold text-[#1C1E21]">
                              <span className={slashClass}>/</span> Cyber AI Analyst
                            </span>
                            <span className="mt-0.5 block text-[13px] text-[#65676B]">
                              From alert to answer in seconds
                            </span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-6 border-t border-[#E4E6EB] pt-6">
                    <p className={colHeaderClass}>Business Software</p>
                    <ul className="mt-3 space-y-0.5 sm:max-w-md">
                      <li>
                        <a
                          href="https://www.koklonya.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={platformRowClass}
                        >
                          <span className="text-[15px] font-semibold text-[#1C1E21]">
                            <span className={slashClass}>/</span> Koklonya
                            <span className={liveBadgeClass}>LIVE</span>
                          </span>
                          <span className="mt-0.5 block text-[13px] text-[#65676B]">
                            Poultry farm ERP — flocks, feed, HR &amp; IFRS-ready books
                          </span>
                        </a>
                      </li>
                    </ul>
                  </div>
                  <div className="mt-6 border-t border-[#E4E6EB] pt-6">
                    <p className={colHeaderClass}>Platform</p>
                    <div className="mt-3 flex flex-wrap gap-6">
                      <Link to="/about" className="text-[15px] font-semibold text-[#0064E0] hover:underline">
                        Why logNsec
                      </Link>
                      <Link to="/products" className="text-[15px] font-semibold text-[#0064E0] hover:underline">
                        See all products
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Solutions mega-menu */}
          <div
            className="relative"
            onMouseEnter={() => openDropdown('solutions')}
            onMouseLeave={scheduleCloseMenu}
          >
            <button type="button" className={triggerClass(openMenu === 'solutions')} aria-expanded={openMenu === 'solutions'}>
              Solutions
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" aria-hidden className="opacity-70">
                <path d="M6 8L2 4h8L6 8z" />
              </svg>
            </button>
            {openMenu === 'solutions' && (
              <div className="absolute left-1/2 top-full z-[100] -translate-x-1/2 pt-2">
                <div className={`${panelClass} w-[min(100vw-2rem,40rem)]`}>
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-10">
                    <div>
                      <p className={colHeaderClass}>By Threat</p>
                      <ul className="space-y-0.5">
                        {[
                          'Ransomware Defence',
                          'Phishing & BEC Protection',
                          'Account Takeover Prevention',
                          'Insider Threat Detection',
                          'Supply Chain Risk',
                          'Financial Fraud & MoMo Scams',
                        ].map((label) => (
                          <li key={label}>
                            <button type="button" className={solutionsRowClass}>
                              {label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className={colHeaderClass}>By Business Size</p>
                      <ul className="space-y-0.5">
                        {[
                          'Startups & Small Business',
                          'Growing SMEs',
                          'Enterprise & Multi-site',
                          'Managed Service Providers',
                          'Government & Public Sector',
                          'Financial Institutions',
                        ].map((label) => (
                          <li key={label}>
                            <button type="button" className={solutionsRowClass}>
                              {label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavLink to="/about" className={linkClass}>
            Why logNsec
          </NavLink>
          <NavLink to="/blog" className={linkClass}>
            Blog
          </NavLink>
          <NavLink to="/contact" className={linkClass}>
            Contact
          </NavLink>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://shieldsme.lognsec.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden rounded-lg bg-[#0064E0] px-5 py-2.5 text-[15px] font-medium text-white transition-colors hover:bg-[#0056C8] md:inline-flex"
          >
            Get Started
          </a>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E4E6EB] text-[#1C1E21] md:hidden"
            aria-expanded={menuOpen}
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-white md:hidden">
          <div className="flex items-center justify-between border-b border-[#E4E6EB] px-5 py-3.5">
            <Logo size="md" />
            <button
              type="button"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E4E6EB]"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex max-h-[calc(100vh-4.5rem)] flex-col overflow-y-auto px-5 py-4">
            {/* Platform accordion */}
            <div className="border-b border-[#E4E6EB]">
              <button
                type="button"
                className="flex w-full items-center justify-between py-4 text-left text-lg font-medium text-[#1C1E21]"
                aria-expanded={mobilePlatformOpen}
                onClick={() => setMobilePlatformOpen((o) => !o)}
              >
                Platform
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`shrink-0 transition-transform ${mobilePlatformOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {mobilePlatformOpen && (
                <div className="pb-4 pl-1">
                  <p className={colHeaderClass}>Protect Your Stack</p>
                  <ul className="mb-4 space-y-1">
                    <li>
                      <a
                        href="https://shieldsme.lognsec.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-lg px-2 py-2 text-[15px] font-medium text-[#1C1E21] hover:bg-[#F0F2F5]"
                        onClick={() => setMenuOpen(false)}
                      >
                        <span className={slashClass}>/</span> ShieldSME
                        <span className={liveBadgeClass}>LIVE</span>
                        <span className="mt-0.5 block text-[13px] font-normal text-[#65676B]">
                          Security health for your business
                        </span>
                      </a>
                    </li>
                    {mobileProtectStackItems.map(([n, s, isNew]) => (
                      <li key={n}>
                        <button
                          type="button"
                          className="block w-full cursor-default rounded-lg border-0 bg-transparent px-2 py-2 text-left text-[15px] font-medium text-[#1C1E21] hover:bg-[#F0F2F5]"
                        >
                          <span className={slashClass}>/</span> {n}
                          {isNew ? <span className={newBadgeClass}>NEW</span> : null}
                          <span className="mt-0.5 block text-[13px] font-normal text-[#65676B]">{s}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className={colHeaderClass}>Strengthen Your Posture</p>
                  <ul className="mb-4 space-y-1">
                    {[
                      ['ExposureIQ', 'Find and fix before attackers do'],
                      ['HumanLayer', 'Your people, defended'],
                      ['SurfaceMap', 'Know your full attack surface'],
                      ['ForensIQ', 'Investigate with precision'],
                      ['ResilienceOS', 'Prepare, respond, recover'],
                    ].map(([n, s]) => (
                      <li key={n}>
                        <button
                          type="button"
                          className="block w-full cursor-default rounded-lg border-0 bg-transparent px-2 py-2 text-left text-[15px] font-medium text-[#1C1E21] hover:bg-[#F0F2F5]"
                        >
                          <span className={slashClass}>/</span> {n}
                          <span className="mt-0.5 block text-[13px] font-normal text-[#65676B]">{s}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className={colHeaderClass}>AI-Powered Intelligence</p>
                  <ul className="mb-4 space-y-1">
                    <li>
                      <button
                        type="button"
                        className="block w-full cursor-default rounded-lg border-0 bg-transparent px-2 py-2 text-left text-[15px] font-medium text-[#1C1E21] hover:bg-[#F0F2F5]"
                      >
                        <span className={slashClass}>/</span> Cyber AI Analyst
                        <span className="mt-0.5 block text-[13px] font-normal text-[#65676B]">
                          From alert to answer in seconds
                        </span>
                      </button>
                    </li>
                  </ul>
                  <p className={colHeaderClass}>Business Software</p>
                  <ul className="mb-4 space-y-1">
                    <li>
                      <a
                        href="https://www.koklonya.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block rounded-lg px-2 py-2 text-[15px] font-medium text-[#1C1E21] hover:bg-[#F0F2F5]"
                        onClick={() => setMenuOpen(false)}
                      >
                        <span className={slashClass}>/</span> Koklonya
                        <span className={liveBadgeClass}>LIVE</span>
                        <span className="mt-0.5 block text-[13px] font-normal text-[#65676B]">
                          Poultry farm ERP — flocks, feed, HR &amp; IFRS-ready books
                        </span>
                      </a>
                    </li>
                  </ul>
                  <div className="border-t border-[#E4E6EB] pt-3">
                    <p className={colHeaderClass}>Platform</p>
                    <Link
                      to="/about"
                      className="mt-2 block py-2 text-[15px] font-semibold text-[#0064E0]"
                      onClick={() => setMenuOpen(false)}
                    >
                      Why logNsec
                    </Link>
                    <Link
                      to="/products"
                      className="block py-2 text-[15px] font-semibold text-[#0064E0]"
                      onClick={() => setMenuOpen(false)}
                    >
                      See all products
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Solutions accordion */}
            <div className="border-b border-[#E4E6EB]">
              <button
                type="button"
                className="flex w-full items-center justify-between py-4 text-left text-lg font-medium text-[#1C1E21]"
                aria-expanded={mobileSolutionsOpen}
                onClick={() => setMobileSolutionsOpen((o) => !o)}
              >
                Solutions
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={`shrink-0 transition-transform ${mobileSolutionsOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {mobileSolutionsOpen && (
                <div className="pb-4 pl-1">
                  <p className={colHeaderClass}>By Threat</p>
                  <ul className="mb-4 space-y-0.5">
                    {[
                      'Ransomware Defence',
                      'Phishing & BEC Protection',
                      'Account Takeover Prevention',
                      'Insider Threat Detection',
                      'Supply Chain Risk',
                      'Financial Fraud & MoMo Scams',
                    ].map((label) => (
                      <li key={label}>
                        <button type="button" className={solutionsRowClass}>
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className={colHeaderClass}>By Business Size</p>
                  <ul className="space-y-0.5">
                    {[
                      'Startups & Small Business',
                      'Growing SMEs',
                      'Enterprise & Multi-site',
                      'Managed Service Providers',
                      'Government & Public Sector',
                      'Financial Institutions',
                    ].map((label) => (
                      <li key={label}>
                        <button type="button" className={solutionsRowClass}>
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className="border-b border-[#E4E6EB] py-4 text-lg font-medium text-[#1C1E21]"
              onClick={() => setMenuOpen(false)}
            >
              Why logNsec
            </Link>
            <Link
              to="/blog"
              className="border-b border-[#E4E6EB] py-4 text-lg font-medium text-[#1C1E21]"
              onClick={() => setMenuOpen(false)}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="border-b border-[#E4E6EB] py-4 text-lg font-medium text-[#1C1E21]"
              onClick={() => setMenuOpen(false)}
            >
              Contact
            </Link>
            <a
              href="https://shieldsme.lognsec.com"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 rounded-lg bg-[#0064E0] py-3.5 text-center text-base font-semibold text-white hover:bg-[#0056C8]"
              onClick={() => setMenuOpen(false)}
            >
              Get Started
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
